/**
 * Centralized filter counting utility for search pages.
 * Single source of truth for which filters are countable and how to count them.
 *
 * @module utils/filterCount
 */
import {
	newAmenities,
	defaultVehicleAmenitiesAndRequirements,
} from '../config/units';

/**
 * Extract countable amenity key from an amenity config object.
 * Supports both 'value' (newAmenities) and 'checkboxQueryParam' (legacy) properties.
 *
 * @param {Object} amenity - Amenity configuration object
 * @returns {string} The amenity key used in search params
 */
const getAmenityKey = (amenity) => amenity.checkboxQueryParam || amenity.value;

/**
 * Storage amenity keys derived from newAmenities (the UI source of truth).
 * Includes all 13 amenities displayed in SearchFilters.
 * @type {string[]}
 */
export const storageAmenityKeys = newAmenities.map(getAmenityKey);

/**
 * Legacy amenity keys that are no longer displayed in SearchFilters UI but
 * are still valid search params (see lib/search/index.js allowedSearchParams).
 * These may exist in bookmarked URLs or external links, so we count them
 * for backwards compatibility.
 * @type {string[]}
 */
export const legacyAmenityKeys = [
	'alarm',
	'cameras',
	'getOpenNow',
	'fencedAndLit',
];

/**
 * Vehicle filter keys derived from defaultVehicleAmenitiesAndRequirements.
 * @type {string[]}
 */
export const vehicleFilterKeys =
	defaultVehicleAmenitiesAndRequirements.map(getAmenityKey);

/**
 * All countable amenity keys - union of storage, vehicle, and legacy keys.
 * Used to ensure any active filter triggers Clear All, regardless of search type.
 * @type {string[]}
 */
export const allAmenityKeys = [
	...new Set([
		...storageAmenityKeys,
		...vehicleFilterKeys,
		...legacyAmenityKeys,
	]),
];

/**
 * Valid vehicle types that can appear in the vehicleTypes param.
 * These correspond to the Car/Boat/RV checkboxes in SearchFilters.
 * @type {string[]}
 */
export const validVehicleTypes = ['car', 'boat', 'rv'];

export const nearMeAmenityKeyMap = {
	'24-hour-storage': 'twentyFourHourAccess',
	'climate-controlled-storage': 'climateControlled',
	'drive-up-storage': 'driveUp',
	'storage-with-electricity': 'power',
	'wine-storage': 'onlyWine',
	'storage-with-free-truck-rental': 'freeTruckRental',
	'mini-storage': '0-25',
	'locker-storage': '0-5',
};

export const nearMeVehicleKeyMap = {
	'boat-storage': 'boat',
	'car-storage': 'car',
	'rv-storage': 'rv',
};

/**
 * Calculate total filter count from search params.
 *
 * Counted filters:
 * - ALL amenity/requirement checkboxes (storage, vehicle, legacy) - counted regardless of searchType
 * - Vehicle type selections (car, boat, rv) - each counts as 1, only for vehicle searchType
 * - Vehicle accommodation selection (outdoor-covered, outdoor-uncovered)
 * - Price filter (minPrice or maxPrice counts as 1)
 * - Specials/deals filter (hasSpecial)
 * - Review filter (averageReview)
 * - Unit size filters (sqft or onlyLocker count as ONE filter for storage)
 * - Vehicle length filter (minLength/maxLength counts as 1, only when no vehicleTypes selected)
 * - Near-me pre-selected amenity or vehicle (counts as 1 if not already in searchParams)
 *
 * @param {Object} searchParams - Current search parameters object
 * @param {string} [searchType='storage'] - Either 'storage' or 'vehicle'
 * @param {Object} [nearMeParams={}] - Near-me params from URL (storageAmenity, vehicle)
 * @returns {number} Total count of active filters
 */
export function calculateFilterCount(
	searchParams,
	searchType = 'storage',
	nearMeParams = {},
) {
	if (!searchParams) return 0;

	let count = 0;

	if (
		Object.keys(nearMeParams).length > 0 &&
		nearMeParams.storageAmenity !== 'storage-deals'
	)
		return 1;

	// 1. Count ALL amenity/requirement filters that are active
	// Using combined keys ensures Clear All activates regardless of how filters were set
	// (e.g., storage filters that persist after switching to vehicle search)
	allAmenityKeys.forEach((key) => {
		if (searchParams[key] === 'true') {
			count++;
		}
	});

	// 2. Vehicle type selections (car, boat, rv checkboxes)
	// Each selected type counts as 1 filter.
	if (searchType === 'vehicle') {
		if (searchParams.vehicleTypes) {
			const selectedTypes = searchParams.vehicleTypes
				.split(',')
				.map((t) => t.trim());
			const validSelectedTypes = selectedTypes.filter((t) =>
				validVehicleTypes.includes(t),
			);
			count += validSelectedTypes.length;
		} else if (
			searchParams.vehicle &&
			validVehicleTypes.includes(searchParams.vehicle)
		) {
			count += 1;
		}
	}

	// 3. Vehicle accommodation (outdoor-covered, outdoor-uncovered)
	if (searchType === 'vehicle' && searchParams.vehicleAccommodation) {
		count++;
	}

	// 4. Price filter (slider sets both min and max, count as 1 filter)
	if (searchParams.minPrice || searchParams.maxPrice) {
		count++;
	}

	// 5. Special/deals filter
	if (searchParams.hasSpecial) {
		count++;
	}

	// 6. Review filter
	if (searchParams.averageReview) {
		count++;
	}

	// 7. Unit size filters
	// For storage: sqft or onlyLocker count as ONE filter
	if (
		searchType === 'storage' &&
		(searchParams.sqft || searchParams.onlyLocker)
	) {
		count++;
	}

	// 8. Vehicle length filter
	// Only count if no vehicleTypes are selected - when vehicleTypes is set,
	// minLength/maxLength are auto-derived from the vehicle type selection
	// and counting them separately would be double-counting
	if (
		searchType === 'vehicle' &&
		!searchParams.vehicleTypes &&
		(searchParams.minLength || searchParams.maxLength)
	) {
		count++;
	}

	// 9. Near-me pre-selected amenity — count only if not already reflected in searchParams
	if (nearMeParams.storageAmenity) {
		const amenityParamKey =
			nearMeAmenityKeyMap[nearMeParams.storageAmenity];
		if (amenityParamKey && !searchParams[amenityParamKey]) {
			count++;
		}
	}

	// 10. Near-me pre-selected vehicle — count only if not already reflected in searchParams
	if (nearMeParams.vehicle) {
		const vehicleType = nearMeVehicleKeyMap[nearMeParams.vehicle];
		const alreadyCounted =
			searchParams.vehicleTypes
				?.split(',')
				.map((t) => t.trim())
				.includes(vehicleType) || searchParams.vehicle === vehicleType;
		if (vehicleType && !alreadyCounted) {
			count++;
		}
	}

	return count;
}
